import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { NoteType, useSetMeetingAdminNote, useSetMeetingNote } from '../../queries/meetings';

interface MeetingNoteEditorProps {
  meetingId: string;
  variant: 'operator' | 'admin';
  note: string | null;
  noteType?: NoteType | null;
}

const NOTE_TYPES: NoteType[] = ['info', 'warning', 'danger'];

const NOTE_TYPE_COLOUR: Record<NoteType, string> = {
  danger: 'text-red-700 dark:text-red-400',
  warning: 'text-orange-700 dark:text-orange-400',
  info: 'text-gray-700 dark:text-gray-300',
};

/** Inline editor for both the operator-facing meeting note (typed info/warning/danger)
 * and the admin-only free-text notes — distinguished by `variant`. */
export function MeetingNoteEditor({ meetingId, variant, note, noteType }: MeetingNoteEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note ?? '');
  const [draftType, setDraftType] = useState<NoteType>(noteType ?? 'info');

  const operatorMutation = useSetMeetingNote();
  const adminMutation = useSetMeetingAdminNote();
  const mutation = variant === 'operator' ? operatorMutation : adminMutation;

  const startEditing = () => {
    setDraft(note ?? '');
    setDraftType(noteType ?? 'info');
    setEditing(true);
  };

  const save = () => {
    const trimmed = draft.trim() || null;
    const promise =
      variant === 'operator'
        ? operatorMutation.mutateAsync({ meetingId, note: trimmed, noteType: draftType })
        : adminMutation.mutateAsync({ meetingId, notes: trimmed });
    promise.then(() => setEditing(false)).catch(() => undefined);
  };

  const label = variant === 'operator' ? 'Note' : 'Admin Notes';
  const colour = variant === 'operator' ? NOTE_TYPE_COLOUR[noteType ?? 'info'] : 'text-purple-700 dark:text-purple-400';

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {label}:{' '}
          {note ? <span className={`font-medium ${colour}`}>{note}</span> : <span className="italic">None</span>}
        </p>
        <button
          type="button"
          onClick={startEditing}
          className="shrink-0 text-sm font-medium text-primary hover:underline"
        >
          {note ? 'Edit' : `+ Add ${label}`}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {variant === 'operator' && (
        <select
          aria-label="Note type"
          value={draftType}
          onChange={(e) => setDraftType(e.target.value as NoteType)}
          className="rounded-md border border-input bg-background px-2 py-1 text-sm"
        >
          {NOTE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t[0].toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      )}
      <textarea
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        autoFocus
      />
      {mutation.isError && (
        <p className="text-sm text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Failed to save.'}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={mutation.isPending}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </button>
        <button type="button" onClick={() => setEditing(false)} className="text-sm text-muted-foreground hover:text-foreground">
          Cancel
        </button>
      </div>
    </div>
  );
}
